<script setup lang="ts">
import { computed } from 'vue'

import { useActionValueFormat } from '../composables/useActionValueFormat'
import type { PlatformActionPreview } from '../models/platformAction.types'
import { camelKey } from '../service/actionLabels'

import { useI18n } from '@/shared/i18n'
import { Alert, Icon } from '@/shared/ui'

const props = defineProps<{ preview: PlatformActionPreview | null }>()

const { t, te } = useI18n()
const { formatValue, formatDelta } = useActionValueFormat()

const rows = computed(() => props.preview?.rows ?? [])
const violations = computed(() => props.preview?.guardrails.violations ?? [])
const delta = computed(() => formatDelta(props.preview?.deltaPct ?? null))

function fieldLabel(field: string): string {
  const key = `platformActions.field.${camelKey(field)}`
  return te(key) ? t(key) : field
}
</script>

<template>
  <section class="action-preview flex flex-col gap-md font-sans">
    <!-- Guardrail reasons come first: they are the reason an approve button may be pointless. -->
    <Alert
      v-if="violations.length > 0"
      tone="danger"
      :title="t('platformActions.preview.violationsTitle')"
    >
      <ul class="action-preview__violations m-0 ps-md">
        <li v-for="violation in violations" :key="violation">{{ violation }}</li>
      </ul>
    </Alert>

    <dl class="action-preview__rows flex flex-col m-0 gap-sm">
      <div
        v-for="row in rows"
        :key="row.field"
        class="action-preview__row flex flex-wrap items-center justify-between py-sm px-md border border-border rounded-lg bg-surface-sunken gap-sm"
      >
        <dt class="action-preview__field text-muted text-[0.875rem]">
          {{ fieldLabel(row.field) }}
        </dt>
        <dd class="action-preview__values inline-flex items-center m-0 text-muted gap-2xs">
          <span class="action-preview__value">{{ formatValue(row.field, row.current) }}</span>
          <Icon name="arrow-right" :size="14" />
          <span class="action-preview__value action-preview__value--next text-fg font-bold">
            {{ formatValue(row.field, row.requested) }}
          </span>
          <span v-if="!row.changed" class="action-preview__unchanged ms-2xs text-[0.75rem]">
            {{ t('platformActions.preview.unchanged') }}
          </span>
        </dd>
      </div>
    </dl>

    <p v-if="delta" class="action-preview__meta m-0 text-muted text-[0.875rem]">
      {{ t('platformActions.preview.delta', { value: delta }) }}
    </p>
    <p v-if="props.preview?.source" class="action-preview__meta m-0 text-muted text-[0.875rem]">
      {{ t('platformActions.preview.source', { source: props.preview.source }) }}
    </p>
  </section>
</template>
